import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { registerCurrentUserForAnalytics, trackPageView } from '../../utils/analytics';
import { sessionAnalytics } from '../../services/analytics/sessionAnalytics.service';
import { createAuditLog } from '../../services/audit/auditLog.service';
import { clientInteractionsService } from '../../services/interactions/clientInteractions.service';
import { logger } from '../../utils/logger';
import { isTargetViewerRole } from '../../utils/authRoles';

const AnalyticsBootstrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  const hasLoggedLogin = useRef(false);

  useEffect(() => {
    registerCurrentUserForAnalytics(user);
    
    // Target viewers cannot write analytics_sessions or audit_logs.
    if (user && isTargetViewerRole(user.role)) {
      return;
    }

    // Ensure analytics session is created when user is authenticated
    if (user) {
      sessionAnalytics.ensureSession(user).catch(error => {
        logger.error('Failed to create analytics session:', error);
      });

      // Only log AUTH_login ONCE per session
      if (!hasLoggedLogin.current) {
        // Create AUTH_login audit log with full user details
        createAuditLog({
          action: 'AUTH_login',
          targetClientId: (user as any).clientId || undefined,
          actorRole: user.role || 'client',
          context: {
            userEmail: user.email || 'unknown',
            userName: user.displayName || user.email?.split('@')[0] || 'Unknown User',
            userRole: user.role || 'client',
            photoURL: user.photoURL || undefined,
            loginPath: location.pathname,
            source: 'analytics_bootstrapper'
          },
          severity: 'info'
        }).catch(error => {
          logger.error('Failed to create AUTH_login audit log:', error);
        });

        // Add client-specific interaction logging for login
        const userClientId = (user as any).clientId;
        if (userClientId) {
          clientInteractionsService.logInteraction(userClientId, {
            type: 'login',
            userId: user.uid,
            userRole: (user.role as 'client' | 'admin') || 'client',
            userEmail: user.email || 'Unknown',
            action: 'User Login',
            details: 'Signed into portal',
            metadata: {
              source: 'login',
              path: location.pathname
            }
          }).catch(error => {
            logger.error('Failed to log client login interaction:', error);
          });
        }

        // Mark login as logged for this session
        hasLoggedLogin.current = true;
      }
    } else {
      // Reset flag on logout so next login will log properly
      hasLoggedLogin.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Trigger only on auth state changes, not navigation (location.pathname intentionally excluded to prevent duplicate logs)

  useEffect(() => {
    if (user && isTargetViewerRole(user.role)) {
      return;
    }
    const pageId = location.pathname;
    trackPageView(pageId, user || undefined);
  }, [location, user]);

  return null;
};

export default AnalyticsBootstrapper;


